import { isEqual } from 'lodash';

import { UserRoles } from '../../../components/roster/types';
import { hasEmail, hasExtUserId, isValidEmail } from '../../../utils/inputValidation';
import { blankStudent } from '../constants/inviteStudentsDefaults';
import { type Student } from '../types/inviteStudentsTypes';

export type EmailErrorKeys = '' | 'required-email-or-ext-user-id' | 'invalid-email' | 'duplicate-email';
export type ExtUserIdErrorKeys = '' | 'required-email-or-ext-user-id' | 'duplicate-ext-user-id';
export type NameErrorKeys = '' | 'required-first-name' | 'required-last-name' | 'invalid-name';
export type RoleErrorKeys = '' | 'invalid-role';

export const findDuplicateEmailIndexes = (students: Student[]) => {
    const seenEmails: string[] = [];
    const indexesOfDuplicates: number[] = [];

    students.forEach((student, index) => {
        if (student.email) {
            if (seenEmails.includes(student.email)) indexesOfDuplicates.push(index);
            else seenEmails.push(student.email);
        }
    });

    return indexesOfDuplicates;
};

export const findDuplicateExtUserIdIndexes = (students: Student[]) => {
    const seenExtUserIds: string[] = [];
    const indexesOfDuplicates: number[] = [];

    students.forEach((student, index) => {
        if (student.extUserId) {
            if (seenExtUserIds.includes(student.extUserId)) indexesOfDuplicates.push(index);
            else seenExtUserIds.push(student.extUserId);
        }
    });

    return indexesOfDuplicates;
};

const isValidName = (name: unknown): boolean => typeof name === 'string';
const hasName = (firstName: string): boolean => firstName.trim().length > 0;

export const isValidRole = (role: any): boolean =>
    [UserRoles.Student, UserRoles.Assistant, UserRoles.Teacher].includes(role.trim().toLowerCase());

export const emailValidation = (email?: string, extUserId?: string): EmailErrorKeys => {
    if (!hasEmail(email) && !hasExtUserId(extUserId)) return 'required-email-or-ext-user-id';
    if (email && !isValidEmail(email)) return 'invalid-email';
    return '';
};

export const extUserIdValidation = (extUserId?: string, email?: string): ExtUserIdErrorKeys => {
    if (!hasEmail(email) && !hasExtUserId(extUserId)) return 'required-email-or-ext-user-id';
    return '';
};

export const firstNameValidation = (firstName: string): NameErrorKeys => {
    if (!isValidName(firstName)) return 'invalid-name';
    if (!hasName(firstName)) return 'required-first-name';

    return '';
};

export const lastNameValidation = (lastName: string): NameErrorKeys => {
    if (!isValidName(lastName)) return 'invalid-name';
    if (!hasName(lastName)) return 'required-last-name';

    return '';
};

export const roleValidation = (role: string | undefined): RoleErrorKeys => {
    if (!role) return '';

    if (!isValidRole(role)) return 'invalid-role';

    return '';
};

export const validateStudentInvitations = (students: Student[]) => {
    const newErrorIndexes: number[] = [];
    const indexesOfDuplicateEmails = findDuplicateEmailIndexes(students);

    students.forEach((student, index) => {
        if (isEqual(student, blankStudent)) {
            // ignore blank rows
            return;
        }

        if (
            !!firstNameValidation(student.firstName) ||
            !!lastNameValidation(student.lastName) ||
            !!emailValidation(student.email, student.extUserId) ||
            !!extUserIdValidation(student.extUserId, student.email) ||
            !!roleValidation(student.role) ||
            indexesOfDuplicateEmails.includes(index)
        ) {
            newErrorIndexes.push(index);
        }
    });

    return newErrorIndexes;
};
